/**
 * Dialog for launching an ad-hoc A/B benchmark: pick benchmark-org flows, define
 * two+ arms (each a set of knob/flag overrides), and run each flow under every
 * arm. Empty arm = baseline (current prod values).
 *
 * @module pages/RunAdHocBenchmarkDialog
 * @see docs/api/services/benchmark.md
 */
import React from 'react';
import {
  Button,
  Dialog,
  DialogTrigger,
  DialogFooter,
  DialogContent,
  TextField,
  NumberField,
  Select,
  Checkbox,
  Text,
} from '@chatsdet/ui';
import { TestTube, Plus, X } from '@phosphor-icons/react';
import {
  useBenchmarkAbWorkflowsQuery,
  useTriggerAdHocBenchmarkMutation,
  type BenchmarkArm,
} from '../useBenchmarkWorkflowData';

type KnobRow = { key: string; value: string };
type FlagVariant = 'on' | 'off' | 'treatment' | 'control';
type FlagRow = { name: string; variant: FlagVariant };
type ArmState = { label: string; knobs: KnobRow[]; flags: FlagRow[] };

const FLAG_VARIANT_OPTIONS = [
  { id: 'treatment', label: 'treatment' },
  { id: 'control', label: 'control' },
  { id: 'on', label: 'on (true)' },
  { id: 'off', label: 'off (false)' },
];

const emptyArm = (label: string): ArmState => ({ label, knobs: [], flags: [] });

/** Coerce a knob override string to boolean/number/string for a cleaner snapshot. */
function parseKnobValue(raw: string): unknown {
  const v = raw.trim();
  if (v === 'true') return true;
  if (v === 'false') return false;
  if (v !== '' && !Number.isNaN(Number(v))) return Number(v);
  return v;
}

function rowsToFlagOverrides(flags: FlagRow[]): BenchmarkArm['flagOverrides'] {
  const out: Record<string, boolean | 'treatment' | 'control'> = {};
  for (const f of flags) {
    const name = f.name.trim();
    if (!name) continue;
    out[name] = f.variant === 'on' ? true : f.variant === 'off' ? false : f.variant;
  }
  return Object.keys(out).length ? out : undefined;
}

function rowsToKnobOverrides(knobs: KnobRow[]): BenchmarkArm['knobOverrides'] {
  const out: Record<string, unknown> = {};
  for (const k of knobs) {
    const key = k.key.trim();
    if (!key) continue;
    out[key] = parseKnobValue(k.value);
  }
  return Object.keys(out).length ? out : undefined;
}

export function RunAdHocBenchmarkDialog() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [runsPerArm, setRunsPerArm] = React.useState<number | null>(3);
  const [arms, setArms] = React.useState<ArmState[]>([emptyArm('A'), emptyArm('B')]);

  const workflowsQuery = useBenchmarkAbWorkflowsQuery(isOpen);
  const triggerMutation = useTriggerAdHocBenchmarkMutation();

  const workflows = workflowsQuery.data?.workflows ?? [];
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? workflows.filter((w) => w.name.toLowerCase().includes(q)) : workflows;
  }, [workflows, search]);

  const labels = arms.map((a) => a.label.trim()).filter(Boolean);
  const labelsUnique = new Set(labels).size === labels.length;
  const canRun =
    selectedIds.size > 0 &&
    labels.length === arms.length &&
    labelsUnique &&
    !!runsPerArm &&
    !triggerMutation.isPending;

  const toggleWorkflow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateArm = (index: number, patch: Partial<ArmState>) => {
    setArms((prev) => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  };

  const handleRun = () => {
    if (!canRun || !runsPerArm) return;
    triggerMutation.mutate({
      workflowIds: [...selectedIds],
      runsPerArm,
      arms: arms.map((a) => ({
        label: a.label.trim(),
        knobOverrides: rowsToKnobOverrides(a.knobs),
        flagOverrides: rowsToFlagOverrides(a.flags),
      })),
    });
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearch('');
      setSelectedIds(new Set());
      setRunsPerArm(3);
      setArms([emptyArm('A'), emptyArm('B')]);
      triggerMutation.reset();
    }
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange} size="lg">
      <Button variant="outline" iconLeft={<TestTube size={16} />}>
        Run A/B
      </Button>
      <Dialog
        title="Run A/B Benchmark"
        description="Run the selected benchmark-org flows under each arm. Leave an arm empty for the current (baseline) configuration."
      >
        <DialogContent>
          {/* Flow selection */}
          <div className="space-y-2">
            <Text size="sm" weight="medium">
              Flows ({selectedIds.size} selected)
            </Text>
            <TextField
              aria-label="Search flows"
              placeholder="Search flows…"
              value={search}
              onChange={setSearch}
            />
            <div className="max-h-44 overflow-y-auto border-y border-border divide-y divide-border">
              {workflowsQuery.isLoading && (
                <Text size="sm" className="text-muted-foreground p-2">
                  Loading flows…
                </Text>
              )}
              {!workflowsQuery.isLoading && filtered.length === 0 && (
                <Text size="sm" className="text-muted-foreground p-2">
                  No flows in the benchmark org. Import flows first.
                </Text>
              )}
              {filtered.map((w) => (
                <label
                  key={w.id}
                  className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted/40"
                >
                  <Checkbox
                    isSelected={selectedIds.has(w.id)}
                    onChange={() => toggleWorkflow(w.id)}
                  />
                  <Text size="sm">{w.name}</Text>
                </label>
              ))}
            </div>
          </div>

          <div className="w-32">
            <NumberField
              label="Runs per arm"
              value={runsPerArm}
              onChange={setRunsPerArm}
              minValue={1}
              maxValue={20}
            />
          </div>

          {/* Arm editors */}
          <div className="space-y-3 border-t border-border pt-3">
            <div className="flex items-center justify-between">
              <Text size="sm" weight="medium" className="text-muted-foreground">
                Arms
              </Text>
              {arms.length < 4 && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<Plus size={14} />}
                  onPress={() =>
                    setArms((prev) => [
                      ...prev,
                      emptyArm(String.fromCharCode(65 + prev.length)),
                    ])
                  }
                >
                  Add arm
                </Button>
              )}
            </div>

            {arms.map((arm, i) => (
              <ArmEditor
                key={i}
                arm={arm}
                canRemove={arms.length > 1}
                onChange={(patch) => updateArm(i, patch)}
                onRemove={() => setArms((prev) => prev.filter((_, j) => j !== i))}
              />
            ))}

            {!labelsUnique && (
              <Text size="xs" className="text-destructive">
                Arm labels must be unique.
              </Text>
            )}
          </div>

          {triggerMutation.isError && (
            <Text className="text-destructive">{triggerMutation.error.message}</Text>
          )}
          {triggerMutation.isSuccess && (
            <div className="rounded-md border border-success/30 bg-success/10 px-4 py-2">
              <Text className="text-success">
                A/B benchmark queued. Job ID: {triggerMutation.data.jobId}
              </Text>
            </div>
          )}
        </DialogContent>

        <DialogFooter>
          <Button variant="outline" onPress={() => handleOpenChange(false)}>
            {triggerMutation.isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!triggerMutation.isSuccess && (
            <Button onPress={handleRun} isDisabled={!canRun} iconLeft={<TestTube size={16} />}>
              {triggerMutation.isPending ? 'Starting…' : 'Run A/B'}
            </Button>
          )}
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}

function ArmEditor(props: {
  arm: ArmState;
  canRemove: boolean;
  onChange: (patch: Partial<ArmState>) => void;
  onRemove: () => void;
}) {
  const { arm, canRemove, onChange, onRemove } = props;

  const setKnob = (idx: number, patch: Partial<KnobRow>) =>
    onChange({ knobs: arm.knobs.map((k, j) => (j === idx ? { ...k, ...patch } : k)) });
  const setFlag = (idx: number, patch: Partial<FlagRow>) =>
    onChange({ flags: arm.flags.map((f, j) => (j === idx ? { ...f, ...patch } : f)) });

  return (
    <div className="rounded-md border border-border p-3 space-y-2 bg-muted/20">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <TextField
            aria-label="Arm label"
            placeholder="Arm label (e.g. A)"
            value={arm.label}
            onChange={(label) => onChange({ label })}
          />
        </div>
        {canRemove && (
          <Button variant="ghost" size="sm" iconLeft={<X size={14} />} onPress={onRemove}>
            Remove
          </Button>
        )}
      </div>

      {/* Knob overrides */}
      <div className="space-y-1.5">
        <Text size="xs" className="text-muted-foreground">
          Knob overrides
        </Text>
        {arm.knobs.map((k, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <TextField
              aria-label="Knob key"
              placeholder="e.g. agent.fast_drive.enabled"
              value={k.key}
              onChange={(key) => setKnob(idx, { key })}
            />
            <div className="w-28">
              <TextField
                aria-label="Knob value"
                placeholder="value"
                value={k.value}
                onChange={(value) => setKnob(idx, { value })}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<X size={12} />}
              onPress={() => onChange({ knobs: arm.knobs.filter((_, j) => j !== idx) })}
            />
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus size={12} />}
          onPress={() => onChange({ knobs: [...arm.knobs, { key: '', value: '' }] })}
        >
          Add knob
        </Button>
      </div>

      {/* Flag overrides */}
      <div className="space-y-1.5">
        <Text size="xs" className="text-muted-foreground">
          Flag overrides
        </Text>
        {arm.flags.map((f, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <TextField
              aria-label="Flag name"
              placeholder="e.g. single-agent-segment"
              value={f.name}
              onChange={(name) => setFlag(idx, { name })}
            />
            <div className="w-36">
              <Select
                aria-label="Flag variant"
                items={FLAG_VARIANT_OPTIONS}
                selectedKey={f.variant}
                onSelectionChange={(key) => setFlag(idx, { variant: String(key) as FlagVariant })}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<X size={12} />}
              onPress={() => onChange({ flags: arm.flags.filter((_, j) => j !== idx) })}
            />
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus size={12} />}
          onPress={() =>
            onChange({ flags: [...arm.flags, { name: '', variant: 'treatment' }] })
          }
        >
          Add flag
        </Button>
      </div>
    </div>
  );
}
